import React, { useMemo, useState } from "react";
import Image from "next/image";
import styled from "styled-components";
import {
  useForm,
  useFieldArray,
  Controller,
  Control,
  FieldErrors,
} from "react-hook-form";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import Slider from "react-slick";
import usStates from "lib/select-options/usStates";
import useIsResMobile from "lib/hooks/useIsResMobile";
import { useLayout } from "lib/contexts/layoutContext";
import TextField from "lib/components/TextField";
import PageLayout from "lib/components/PageLayout";
import ContentArea from "lib/components/ContentArea";
import Select from "lib/components/Select";
import ImgConsultation from "lib/assets/consultation.png";
import ImgScreenConsultation1 from "lib/assets/screen-consultation-1.png";
import ImgScreenConsultation2 from "lib/assets/screen-consultation-2.png";
import ImgScreenConsultation3 from "lib/assets/screen-consultation-3.png";
import { useTheme } from "lib/contexts/themeContext";
import Button from "lib/components/Button";
import { isValidEmail } from "domain/auth/isValidEmail";
import Fetch from "lib/fetch";
import { SpinFixed } from "lib/components/Spin";
import Head from "lib/components/Head";

const fetch = new Fetch();

interface FormJoin {
  firstName: string;
  lastName: string;
  phoneNumber: string;
  email: string;
  licenses: FormLicense[];
  website: string;
}
interface FormLicense {
  state?: string;
  status?: string;
  year?: string;
}

const listUsStates = usStates.map(({ value }) => ({
  label: value,
  key: value,
  value,
}));

/**
 * Produces a list of years.
 * @returns {string[]}
 * @example ["2020".."1921"]
 */
const makeListYear = (): string[] => {
  const listYear = [];
  const thisYear = new Date().getFullYear();
  for (let i = 0; i < 100; i++) {
    listYear.push(String(thisYear - i));
  }
  return listYear;
};

const EMPTY_FORM_LICENSE = {
  state: "",
  status: "",
  year: "",
};
const EMPTY_FORM_JOIN = {
  firstName: "",
  lastName: "",
  phoneNumber: "",
  email: "",
  licenses: [EMPTY_FORM_LICENSE],
  website: "",
};
const LIST_YEAR = makeListYear();

const SectionTitle = styled.h1`
  margin: 0;
  line-height: 1.2;
  font-size: 50px;
  font-weight: bold;
  text-align: center;
  white-space: pre-line;
`;

const StyledSlider = styled(Slider)`
  .slick-list {
    overflow: visible;
  }
`;

const PagingDot = styled("div")<{ active: boolean }>`
  border-radius: 4px;
  width: 8px;
  height: 8px;
  background-color: ${({ active }) => (active ? `#195dee` : `#c7cadc`)};
`;

const Paging = ({ active }: { active: number }) => {
  return (
    <Flex
      css={`
        padding-bottom: 30px;
        width: 48px;
        justify-content: space-between;
        align-self: center;
      `}
    >
      <PagingDot active={active === 0} />
      <PagingDot active={active === 1} />
      <PagingDot active={active === 2} />
    </Flex>
  );
};

const ScreenShot = ({ src }: { src: string }) => {
  return (
    <div
      css={`
        display: flex;
        justify-content: center;
      `}
    >
      <div
        css={`
          overflow: hidden;
          margin: 0 15px;
          border-top-left-radius: 30px;
          border-top-right-radius: 30px;
          height: auto;
          display: flex;
          max-width: 560px;
          box-shadow: 20px 10px 50px 0 rgba(199, 202, 220, 0.5);
        `}
      >
        <div
          css={`
            margin-top: -7%;
            margin-bottom: -17%;
          `}
        >
          <Image src={src} width={560} height={1151} alt="" />
        </div>
      </div>
    </div>
  );
};

interface FieldLicenseProps {
  control: Control;
  errors: FieldErrors;
  index?: number;
  name: string;
}

const FieldLicense: React.FC<FieldLicenseProps> = ({
  control,
  name,
  index,
  errors,
}) => {
  const namePrefix = useMemo(() => {
    return `${name}${index !== undefined ? `.${index}` : ""}`;
  }, [index, name]);
  const errorObject = useMemo(() => {
    if (index !== undefined) {
      return errors[name]?.[index];
    } else {
      return errors[name];
    }
  }, [name, index, errors]);
  return (
    <div
      css={`
        margin-bottom: 15px;
        display: flex;
      `}
    >
      <div
        css={`
          margin-right: 10px;
          flex: 1;
        `}
      >
        <div
          css={`
            padding-bottom: 10px;
            font-size: 14px;
            font-weight: bold;
          `}
        >
          State
        </div>
        <Controller
          control={control}
          name={`${namePrefix}.state`}
          rules={{ required: true }}
          render={(props) => (
            <Select
              placeholder="State"
              {...props}
              options={listUsStates}
              error={!!errorObject?.state}
              helperText={errorObject?.state?.message}
            />
          )}
        />
      </div>
      <div
        css={`
          margin-right: 10px;
          flex: 1;
        `}
      >
        <div
          css={`
            padding-bottom: 10px;
            font-size: 14px;
            font-weight: bold;
          `}
        >
          Status
        </div>
        <Controller
          control={control}
          name={`${namePrefix}.status`}
          rules={{ required: true }}
          render={(props) => (
            <Select
              placeholder="Status"
              {...props}
              options={[
                { value: "Active", label: "Active", key: "Active" },
                { value: "Inactive", label: "Inactive", key: "Inactive" },
              ]}
              error={!!errorObject?.status}
              helperText={errorObject?.status?.message}
            />
          )}
        />
      </div>
      <div
        css={`
          flex: 1;
        `}
      >
        <div
          css={`
            padding-bottom: 10px;
            font-size: 14px;
            font-weight: bold;
          `}
        >
          Year
        </div>
        <Controller
          control={control}
          name={`${namePrefix}.year`}
          rules={{ required: true }}
          render={(props) => (
            <Select
              placeholder="Year"
              {...props}
              options={LIST_YEAR.map((year) => ({
                value: year,
                label: year,
                key: year,
              }))}
              error={!!errorObject?.year}
              helperText={errorObject?.year?.message}
            />
          )}
        />
      </div>
    </div>
  );
};

const ForLawyers = () => {
  const [active, setActive] = useState(0);

  const onAfterChange = (index: number) => {
    setActive(index);
  };

  const isResMobile = useIsResMobile();
  const theme = useTheme();
  const { doOpenAlert } = useLayout();

  const {
    control,
    handleSubmit,
    reset,
    formState: { isSubmitting, errors },
  } = useForm<FormJoin>({
    defaultValues: EMPTY_FORM_JOIN,
  });

  const { fields, append } = useFieldArray({
    control,
    name: "licenses",
  });

  const handleOnSubmit = handleSubmit(
    async ({ firstName, lastName, website, licenses, email }) => {
      const sanitizedFieldLicenses = licenses
        .map((x) => `${x.state}, ${x.status}, ${x.year}`)
        .join("\n");

      try {
        await submitForm();
        doOpenAlert({
          content: "Form has been successfully submitted!",
          onConfirm: () => reset(),
        });
        // RESET FORM
      } catch (e) {
        console.error("form submit error", e);
      }

      async function submitForm() {
        return await fetch.post("https://formspree.io/f/xvowzddp", {
          firstName,
          lastName,
          website,
          email,
          licenses: sanitizedFieldLicenses,
        });
      }
    },
  );

  return (
    <PageLayout>
      {isSubmitting && <SpinFixed />}
      <Head
        title="For Lawyers | Lawfully"
        desc="Get more potential clients. Our platform helps immigrants find the
        best lawyers for their case using our searching algorithm. Lawyers can
        provide consultation for their new clients with video or text
        chatting."
        url="/for-lawyers"
      />
      <div
        css={`
          width: 100%;
          position: relative;
          background-color: #325aff;
          padding-top: ${isResMobile ? `40px` : `80px`};
          padding-bottom: ${isResMobile ? `20px` : `0`};
        `}
        id="contact"
      >
        <ContentArea>
          <Flex
            css={`
              width: 100%;
              flex-direction: column;
              align-items: center;
              padding-bottom: 20px;
              color: white;
            `}
          >
            <SectionTitle
              css={`
                font-size: ${isResMobile ? `26px` : `50px`};
                line-height: ${isResMobile ? `1.46` : `1.2`};
              `}
            >
              <Flex
                css={`
                  flex-direction: ${isResMobile ? `column` : `row`};
                `}
              >
                <span
                  css={`
                    white-space: pre;
                  `}
                >
                  Get more{" "}
                </span>
                <span>potential clients</span>
              </Flex>
            </SectionTitle>
            <p
              css={`
                max-width: ${isResMobile ? `100%` : `840px`};
                font-size: ${isResMobile ? `13px` : `18px`};
                line-height: 1.67;
                text-align: center;
                margin-top: ${isResMobile ? `20px` : `30px`};
                margin-bottom: ${isResMobile ? `41px` : `80px`};
              `}
            >
              Our platform helps immigrants find the best lawyers for their case
              using our searching algorithm. Lawyers can provide consultation
              for their new clients with video or text chatting.
            </p>
            <div
              css={`
                width: 100%;

                margin: ${isResMobile ? `0 -8px` : `0`};
              `}
            >
              <Image src={ImgConsultation} width={1848} height={1106} />
            </div>
          </Flex>
        </ContentArea>
      </div>

      {/* How clients request */}

      <div
        css={`
          background-color: #f3f4f8;
          padding-top: ${isResMobile ? `40px` : `80px`};
        `}
      >
        <ContentArea
          css={`
            display: flex;
            justify-content: center;
          `}
        >
          <Flex
            css={`
              width: 100%;
              margin: ${isResMobile ? `0` : `0 -33px`};
              flex: 1;
              height: auto;
              flex-direction: column;
            `}
          >
            <SectionTitle
              css={`
                padding-bottom: ${isResMobile ? `30px` : `60px`};
                font-size: ${isResMobile ? `26px` : `50px`};
                line-height: ${isResMobile ? `1.46` : `1.2`};
              `}
            >
              How clients request
            </SectionTitle>
            <div
              css={`
                display: ${isResMobile ? `flex` : `none`};
                align-self: center;
              `}
            >
              <Paging active={active} />
            </div>
            <StyledSlider
              slidesToShow={3}
              centerMode
              infinite={false}
              arrows={false}
              centerPadding={`18px`}
              responsive={[
                {
                  breakpoint: theme.breakpoints.values.md,
                  settings: { slidesToShow: 1 },
                },
              ]}
              afterChange={onAfterChange}
            >
              <ScreenShot src={ImgScreenConsultation1} />
              <ScreenShot src={ImgScreenConsultation2} />
              <ScreenShot src={ImgScreenConsultation3} />
            </StyledSlider>
          </Flex>
        </ContentArea>
      </div>

      {/* Join Lawfully */}

      <div
        css={`
          background-color: white;
          padding-top: ${isResMobile ? `40px` : `80px`};
          padding-bottom: ${isResMobile ? `40px` : `90px`};
        `}
      >
        <ContentArea
          css={`
            px: ${isResMobile ? `20px` : `30px`};
          `}
        >
          <Flex
            css={`
              flex-direction: column;
              align-items: center;
            `}
          >
            <SectionTitle
              css={`
                line-height: 1.2;
                font-size: ${isResMobile ? `26px` : `50px`};
              `}
              id="join-now"
            >
              {`Join Lawfully now
                  (for lawyers only)`}
            </SectionTitle>
            <div
              css={`
                margin-top: 40px;
                width: ${isResMobile ? `100%` : `400px`};
                flex-shrink: 1;
              `}
            >
              <form onSubmit={handleOnSubmit}>
                <label
                  css={`
                    display: block;
                    padding-bottom: 18px;
                  `}
                >
                  <div
                    css={`
                      margin-bottom: 14px;
                      line-height: 1.33;
                      font-size: 18px;
                      font-weight: bold;
                    `}
                  >
                    Name
                  </div>
                  <Flex>
                    <div
                      css={`
                        flex: 1;
                        padding-right: 10px;
                      `}
                    >
                      <Controller
                        name="firstName"
                        rules={{ required: true }}
                        control={control}
                        render={(props) => (
                          <StyledTextField
                            fullWidth
                            placeholder="Enter"
                            error={!!errors.firstName}
                            helperText={errors.firstName?.message}
                            {...props}
                          />
                        )}
                      />
                    </div>
                    <div
                      css={`
                        flex: 1;
                      `}
                    >
                      <Controller
                        name="lastName"
                        rules={{ required: true }}
                        control={control}
                        render={(props) => (
                          <StyledTextField
                            fullWidth
                            placeholder="Enter"
                            error={!!errors.lastName}
                            helperText={errors.lastName?.message}
                            {...props}
                          />
                        )}
                      />
                    </div>
                  </Flex>
                </label>
                <label
                  css={`
                    display: block;
                    padding-bottom: 18px;
                  `}
                >
                  <div
                    css={`
                      margin-bottom: 14px;
                      line-height: 1.33;
                      font-size: 18px;
                      font-weight: bold;
                    `}
                  >
                    Your phone number
                  </div>
                  <Controller
                    name="phoneNumber"
                    rules={{ required: true }}
                    control={control}
                    render={(props) => (
                      <StyledTextField
                        type="tel"
                        fullWidth
                        placeholder="Enter"
                        error={!!errors.phoneNumber}
                        helperText={errors.phoneNumber?.message}
                        {...props}
                      />
                    )}
                  />
                </label>
                <label
                  css={`
                    display: block;
                    padding-bottom: 18px;
                  `}
                >
                  <div
                    css={`
                      margin-bottom: 14px;
                      line-height: 1.33;
                      font-size: 18px;
                      font-weight: bold;
                    `}
                  >
                    Email
                  </div>
                  <Controller
                    name="email"
                    rules={{
                      required: true,
                      validate: (val) =>
                        isValidEmail(val) ? true : "Invalid email address",
                    }}
                    control={control}
                    render={(props) => (
                      <StyledTextField
                        fullWidth
                        placeholder="Enter"
                        error={!!errors.email}
                        helperText={errors.email?.message}
                        {...props}
                      />
                    )}
                  />
                </label>
                <div
                  css={`
                    display: block;
                    padding-bottom: 18px;
                  `}
                >
                  <div
                    css={`
                      margin-bottom: 14px;
                      line-height: 1.33;
                      font-size: 18px;
                      font-weight: bold;
                    `}
                  >
                    Bar admission
                  </div>
                  {fields.map((field, index) => (
                    <FieldLicense
                      key={field.id}
                      name={"licenses"}
                      control={control}
                      index={index}
                      errors={errors}
                    />
                  ))}
                  <Button
                    size="small"
                    neutral
                    width="220px"
                    onClick={() => append(EMPTY_FORM_LICENSE)}
                  >
                    Add another
                  </Button>
                </div>
                <label
                  css={`
                    display: block;
                    padding-bottom: 18px;
                  `}
                >
                  <div
                    css={`
                      margin-bottom: 14px;
                      line-height: 1.33;
                      font-size: 18px;
                      font-weight: bold;
                    `}
                  >
                    Your website (or LinkedIn) address
                  </div>
                  <Controller
                    name="website"
                    rules={{ required: true }}
                    control={control}
                    render={(props) => (
                      <StyledTextField
                        fullWidth
                        placeholder="Enter"
                        error={!!errors.website}
                        helperText={errors.website?.message}
                        {...props}
                      />
                    )}
                  />
                </label>
                <Button id="ForLawyers_JoinNow_Submit" type="submit">
                  GET STARTED
                </Button>
              </form>
            </div>
          </Flex>
        </ContentArea>
      </div>
    </PageLayout>
  );
};

export default ForLawyers;

const Flex = styled("div")`
  display: flex;
`;

const StyledTextField = styled(TextField)`
  &&& {
    margin-bottom: 10px;
  }
`;
